import React, { useMemo, useState } from 'react';
import { Button, Paper, Box, Typography, TextField, Grid, IconButton } from 'components/mui';
import { DeleteIcon } from 'components/icons';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { useAnnouncementFormUnified } from 'containers/pages/announcementManagement/hooks';
import * as TY from 'containers/pages/announcementManagement/constants';
import LoadingBackdrop from 'components/LoadingBackdrop';
import ConfirmationDialog from 'components/confirmationDialog/ConfirmationDialog';
import { Announcement } from 'containers/pages/announcementManagement/types';

interface AnnouncementFormUnifiedProps {
  mode: 'add' | 'edit';
  announcement?: Announcement | null;
  onSaveSuccess?: () => void;
  onDelete?: (id: number) => Promise<void>;
}

function MissingFieldsHint({ canNotSave, missing }: { canNotSave: boolean; missing: string[] }) {
  if (!canNotSave || missing.length === 0) return null;
  return (
    <Typography color="error" variant="body2" sx={{ mb: 1, textAlign: 'right' }}>
      Please fill in all required fields: {missing.join(', ')}.
    </Typography>
  );
}

export default function AnnouncementFormUnified({
  mode,
  announcement,
  onSaveSuccess,
  onDelete,
}: AnnouncementFormUnifiedProps) {
  const { state, handler } = useAnnouncementFormUnified({
    mode,
    announcement,
    onSaveSuccess,
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const form = state.announcement as any;

  const missingFields = useMemo(() => {
    const missing: string[] = [];
    if (!form[TY.TITLE]?.trim()) missing.push('Title');
    if (!form[TY.DESCRIPTION]?.trim()) missing.push('Description');
    if (!form[TY.EXPIRE_DATE]) missing.push('Expire Date');
    return missing;
  }, [form]);

  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!announcement?.ItemID || !onDelete) return;
    setIsDeleting(true);
    try {
      await onDelete(announcement.ItemID);
      setDeleteDialogOpen(false);
    } catch {
      // Error handled in parent
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteClose = () => {
    if (isDeleting) return;
    setDeleteDialogOpen(false);
  };

  return (
    <Box>
      <Paper elevation={3} sx={{ borderRadius: 2, padding: 3 }}>
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
          {mode === 'add' ? 'New Announcement' : 'Edit Announcement'}
        </Typography>
        <Box display="flex" flexDirection="column" gap={2}>
          {/* Title + Expire Date row */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={9}>
              <TextField
                label="Title"
                name={TY.TITLE}
                value={form[TY.TITLE] || ''}
                onChange={handler.onForm}
                required
                error={state.valid.Title.isErr}
                helperText={state.valid.Title.errMsg || `${(form[TY.TITLE] || '').length}/150`}
                fullWidth
                size="small"
                inputProps={{ maxLength: 150 }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Expire Date *"
                  value={form[TY.EXPIRE_DATE] ? dayjs(form[TY.EXPIRE_DATE]) : null}
                  onChange={handler.onDateChange}
                  inputFormat="YYYY-MM-DD"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      fullWidth
                      error={state.valid.ExpireDate.isErr}
                      helperText={state.valid.ExpireDate.errMsg}
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>

          {/* Description - full width */}
          <TextField
            label="Description"
            name={TY.DESCRIPTION}
            value={form[TY.DESCRIPTION] || ''}
            onChange={handler.onForm}
            required
            error={state.valid.Description.isErr}
            helperText={state.valid.Description.errMsg || `${(form[TY.DESCRIPTION] || '').length}/2000`}
            fullWidth
            size="small"
            multiline
            minRows={4}
            inputProps={{ maxLength: 2000 }}
          />

          {/* Read More Link */}
          <TextField
            label="Read More Link (optional)"
            name={TY.MORE_LINK}
            value={form[TY.MORE_LINK] || ''}
            onChange={handler.onForm}
            fullWidth
            size="small"
            placeholder="https://..."
          />
        </Box>
      </Paper>

      {/* Action Row */}
      <Box display="flex" gap={2} mt={1} mb={2} alignItems="flex-start">
        {/* Delete button - only in edit mode */}
        {mode === 'edit' && onDelete && (
          <IconButton
            aria-label="Delete announcement"
            color="error"
            onClick={handleDeleteClick}
            size="small"
          >
            <DeleteIcon />
          </IconButton>
        )}
        <Box flex="1" />
        <Box display="flex" flexDirection="column" alignItems="flex-end">
          <MissingFieldsHint canNotSave={state.canNotSave} missing={missingFields} />
          <Box display="flex" gap={2}>
            <Button variant="outlined" onClick={handler.onReset}>
              RESET
            </Button>
            <Button
              variant="contained"
              disabled={state.canNotSave}
              onClick={handler.onSave}
              title={
                state.canNotSave && missingFields.length > 0
                  ? `Please fill in: ${missingFields.join(', ')}`
                  : undefined
              }
            >
              {mode === 'edit' ? 'SAVE' : 'CREATE'}
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Delete Confirmation Dialog */}
      {mode === 'edit' && (
        <ConfirmationDialog
          open={deleteDialogOpen}
          onClose={handleDeleteClose}
          onConfirm={handleDeleteConfirm}
          title="Delete Announcement"
          description="Are you sure you want to delete this announcement? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          loading={isDeleting}
        />
      )}

      <LoadingBackdrop open={state.isLoading} />
    </Box>
  );
}
